import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, TrendingUp, Shield, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/authStore'

type LoginFormData = { username: string; password: string }

const LoginPage = () => {
  const { t } = useTranslation('auth')
  const [showPassword, setShowPassword] = useState(false)
  const { login } = useAuthStore()

  const loginSchema = z.object({
    username: z.string().min(1, t('auth:usernameRequired')),
    password: z.string().min(1, t('auth:passwordRequired')),
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // 이미 토큰이 있으면 대시보드로 리다이렉트(스토어 초기화 전 무한 루프 방지)
  if (localStorage.getItem('admin_token')) {
    return <Navigate to="/dashboard" replace />
  }

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data.username, data.password)
      // After successful login, navigation will be handled by useEffect
    } catch (error) {
      // Error handling is done in the store
    }
  }

  const togglePasswordVisibility = () => setShowPassword(!showPassword)

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
      {/* Background animation */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating shapes */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-64 h-64 bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-full blur-3xl"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              ease: 'linear',
            }}
            style={{
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-glass-lg">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            KRYZONIC
          </h1>
          <p className="text-dark-300">
            {t('auth:loginSubtitle')}
          </p>
        </motion.div>

        {/* Login Form */}
        <motion.div
          className="glass-card p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-dark-200 mb-2">
                {t('auth:username')}
              </label>
              <input
                {...register('username')}
                type="text"
                id="username"
                className="input-glass w-full"
                placeholder={t('auth:usernamePlaceholder')}
                autoComplete="username"
                disabled={isSubmitting}
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-400">
                  {errors.username.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-dark-200 mb-2">
                {t('auth:password')}
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  className="input-glass w-full pr-12"
                  placeholder={t('auth:passwordPlaceholder')}
                  autoComplete="current-password"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-dark-400 hover:text-dark-200 transition-colors"
                  disabled={isSubmitting}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary relative overflow-hidden"
              whileTap={{ scale: 0.98 }}
            >
              <span className={`${isSubmitting ? 'opacity-0' : 'opacity-100'} transition-opacity`}>
                {t('auth:login')}
              </span>
              {isSubmitting && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
              )}
            </motion.button>
          </form>
        </motion.div>

        {/* Features */}
        <motion.div
          className="mt-8 space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="glass-card p-4 text-center">
              <Shield className="w-6 h-6 text-primary-400 mx-auto mb-2" />
              <p className="text-sm text-dark-300 font-medium">{t('auth:security')}</p>
            </div>
            <div className="glass-card p-4 text-center">
              <TrendingUp className="w-6 h-6 text-success-400 mx-auto mb-2" />
              <p className="text-sm text-dark-300 font-medium">{t('auth:realtime')}</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Zap className="w-6 h-6 text-warning-400 mx-auto mb-2" />
              <p className="text-sm text-dark-300 font-medium">{t('auth:performance')}</p>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          className="text-center mt-8 text-sm text-dark-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          © 2025 KRYZONIC. {t('auth:allRightsReserved')}
        </motion.div>
      </div>
    </div>
  )
}

export default LoginPage